<template>
  <div class="complex-filter">
    <ComplexFilterSection
      v-if="showOwnFields && (filterMode !== 'readOnly' || hasOwnFilters)"
      :model-value="ownGroups"
      title="My Fields"
      :org="currentOrg"
      :fields="ownFieldsNormalized"
      :show-header="partnerMode !== 'none'"
      :disabled="disabled"
      :filter-mode="filterMode"
      @update:model-value="updateOwnGroups"
    />

    <template v-for="(p, index) in partners" :key="p.partner.id">
      <ComplexFilterSection
        v-if="filterMode !== 'readOnly' || hasPartnerFilters(p.partner.id)"
        :model-value="getPartnerGroups(p.partner.id)"
        :title="p.partner.name"
        :org="p.partner"
        :fields="p.fields"
        :deletable="partnerMode === 'multi'"
        :show-header="showOwnFields || partnerMode === 'multi'"
        :show-border="showOwnFields || index > 0"
        :disabled="disabled"
        :filter-mode="filterMode"
        @update:model-value="
          (groups) => updatePartnerGroups(p.partner.id, groups)
        "
        @delete="$emit('delete:partner', p.partner.id)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import useAuth from '@/composables/useAuth';
import type { Partner } from '@/types/partners';

import ComplexFilterSection, {
  type FilterGroupOutput,
} from './ComplexFilterSection.vue';
import {
  type DataSource,
  type FilterMode,
  type GroupedFieldOption,
  type OwnFieldsConfig,
} from './types';

export type { DataSource, FilterMode, GroupedFieldOption, OwnFieldsConfig };

export type PartnerFieldsConfig = {
  partner: Partner;
  fields: GroupedFieldOption[];
};

export type PartnerGroupOutput = {
  partnerId: number;
  groups: FilterGroupOutput[];
};

export type ComplexFilterOutput = {
  ownGroups: FilterGroupOutput[];
  partnerGroups?: PartnerGroupOutput[];
};

export type PartnerMode = 'none' | 'single' | 'multi';

const props = withDefaults(
  defineProps<{
    ownFields: OwnFieldsConfig;
    partners?: PartnerFieldsConfig[];
    partnerMode: PartnerMode;
    disabled?: boolean;
    filterMode?: FilterMode;
    showOwnFields?: boolean;
  }>(),
  { showOwnFields: true, partners: () => [], filterMode: 'editable' },
);

defineEmits<{
  'delete:partner': [partnerId: number];
}>();

const model = defineModel<ComplexFilterOutput | null>();
const { currentOrg } = useAuth();

const ownFieldsNormalized = computed<GroupedFieldOption[]>(() => {
  const raw = props.ownFields;
  const first = raw?.at(0);
  if (!first) return [];

  // Already normalized (Partner style or simple Own fields)
  if ('options' in first) {
    return raw as GroupedFieldOption[];
  }

  // Normalize DataSource[] (Own fields with CRMs) to GroupedFieldOption[]
  return (raw as DataSource[]).flatMap((ds) =>
    ds.groups.map((g) => ({
      id: g.sourceId,
      label: g.title,
      options: g.fields,
      dataSourceLabel: ds.label,
      allowNesting: g.allowNesting,
    })),
  );
});

const ownGroups = computed(() => model.value?.ownGroups ?? []);

const hasOwnFilters = computed(() => {
  return ownGroups.value.some((group) => group.filters.length > 0);
});

function getPartnerGroups(partnerId: number): FilterGroupOutput[] {
  return (
    model.value?.partnerGroups?.find((p) => p.partnerId === partnerId)
      ?.groups ?? []
  );
}

function hasPartnerFilters(partnerId: number): boolean {
  const groups = getPartnerGroups(partnerId);
  return groups.some((group) => group.filters.length > 0);
}

function updateOwnGroups(groups: FilterGroupOutput[]) {
  const partnerGroups = model.value?.partnerGroups;
  const isEmpty = groups.length === 0 && !partnerGroups?.length;

  model.value = isEmpty
    ? null
    : {
        ownGroups: groups,
        ...(partnerGroups?.length && { partnerGroups }),
      };
}

function updatePartnerGroups(partnerId: number, groups: FilterGroupOutput[]) {
  const ownGroups = model.value?.ownGroups ?? [];
  const currentPartners = model.value?.partnerGroups ?? [];
  const exists = currentPartners.some((p) => p.partnerId === partnerId);

  let newPartnerGroups: PartnerGroupOutput[];

  if (groups.length === 0) {
    // Remove partner entry
    newPartnerGroups = currentPartners.filter((p) => p.partnerId !== partnerId);
  } else if (exists) {
    // Update existing partner in place
    newPartnerGroups = currentPartners.map((p) =>
      p.partnerId === partnerId ? { partnerId, groups } : p,
    );
  } else {
    // Append new partner
    newPartnerGroups = [...currentPartners, { partnerId, groups }];
  }

  const isEmpty = ownGroups.length === 0 && newPartnerGroups.length === 0;

  model.value = isEmpty
    ? null
    : {
        ownGroups,
        ...(newPartnerGroups.length > 0 && { partnerGroups: newPartnerGroups }),
      };
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.complex-filter {
  @apply flex flex-col gap-16;
}
</style>
